import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EventIcon from '@mui/icons-material/Event';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import EditIcon from '@mui/icons-material/Edit';
import WcOutlinedIcon from '@mui/icons-material/WcOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Passenger } from '../../../data/models/passenger.model';
import { useAddress } from '../../../hooks/display/useAddress';
import { useDate } from '../../../hooks/display/useDate';
import { usePassengerPhone } from '../../../hooks/display/usePassengerPhone';
import { useGender } from '../../../hooks/display/useGender';
import { ServicePlan } from '../../../data/models/service-plan.model';
import PassengerBenefits from './PassengerBenefits';
import PassengerNamedLocations from './PassengerNamedLocations';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';
import { useFeatureMemberPreferences } from '../../../hooks/features/useFeatureMemberPreferences';
import PassengerPreferences from './PassengerPreferences';
import { usePortalName } from '../../../hooks/usePortalName';
import { EDIT_MEMBER_PROFILE_NOT_ALLOWED_PORTAL_NAMES } from '../../../data/constants';
import * as passengerSlice from '../../../data/passenger.slice';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';

const PREFIX = 'PassengerProfile';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  row: `${PREFIX}-row`,
  iconAndText: `${PREFIX}-icon-and-text`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
  benefits: `${PREFIX}-benefits`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    minWidth: 444,
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    minHeight: 40,
  },

  [`& .${classes.row}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  },

  [`& .${classes.iconAndText}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },

  [`& .${classes.label}`]: {
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: theme.palette.lightTextPrimary.main,
  },

  [`& .${classes.value}`]: {
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '0.1px',
    textAlign: 'right',
    color: theme.palette.lightTextPrimary.main,
  },

  [`& .${classes.benefits}`]: {
    marginTop: theme.spacing(2),
  },
}));

interface PassengerProfileProps {
  passenger: Passenger | null;
  servicePlans: ServicePlan[];
  edit: (passenger: Passenger) => void;
  readonly: boolean;
}

export default function PassengerProfile({
  passenger,
  servicePlans,
  edit,
  readonly,
}: PassengerProfileProps) {
  const t = useCommonTranslation();
  const portalName = usePortalName() || '';
  const dispatch = useAppDispatch();

  const isMemberPreferencesEnabled = useFeatureMemberPreferences();
  const isSuperReaderUser = useIsSuperReaderUser();

  const landlinePhone = usePassengerPhone(
    passenger?.passengerPhones,
    'LANDLINE',
  );
  const naPhone = usePassengerPhone(passenger?.passengerPhones, 'NA');
  const passengerPreferences = useAppSelector(
    passengerSlice.selectCurrentPassengerPreferences,
  );

  useEffect(() => {
    if (passenger) {
      dispatch(
        passengerSlice.fetchPassengerPreferencesAsync(passenger.passengerUUID!),
      );
    }
  }, [passenger]);

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography variant="h3">{t('passenger.profile')}</Typography>
          {!readonly &&
            !!passenger?.manual &&
            !isSuperReaderUser &&
            !EDIT_MEMBER_PROFILE_NOT_ALLOWED_PORTAL_NAMES.includes(
              portalName,
            ) && (
              <Tooltip title={t('passenger.editProfile')}>
                <span>
                  <IconButton
                    data-testid="delete-address"
                    onClick={(evt) => edit(passenger!)}
                    color="primary"
                    disabled={!passenger}>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <PersonOutlineOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.clientPassengerId')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {passenger?.clientPassengerID}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <EventIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.dob')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {useDate(passenger?.dateOfBirth)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <WcOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.gender')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {useGender(passenger?.genderCode)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <PlaceOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.address')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {passenger?.passengerAddresses?.find(
              (addr) => addr.passengerAddressUseBC === 'MANUAL',
            )
              ? useAddress(
                  passenger?.passengerAddresses?.find(
                    (addr) => addr.passengerAddressUseBC === 'MANUAL',
                  ),
                )
              : useAddress(passenger?.passengerAddresses?.[0])}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <CallOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.phoneNumber')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {landlinePhone || naPhone}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <PhoneIphoneRoundedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.mobile')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {usePassengerPhone(passenger?.passengerPhones, 'MOBILE')}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <MailOutlineRoundedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.email')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {passenger?.passengerEmailAddress || undefined}
          </Typography>
        </Box>
      </Paper>
      {isMemberPreferencesEnabled && (
        <PassengerPreferences
          passengerUUID={passenger?.passengerUUID!}
          passengerPreferences={passengerPreferences}
        />
      )}
      <PassengerBenefits servicePlans={servicePlans} passenger={passenger} />
      <PassengerNamedLocations passenger={passenger} />
    </StyledBox>
  );
}
